import { addDays, formatISO, isWeekend, parseISO } from 'date-fns'
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryState
} from 'nuqs'

const QUERY_KEYS = {
  date: 'date',
  assigned: 'assigned',
  shipVia: 'ship-via',
  shipDate: 'ship-date',
  orderView: 'order-view',
  search: 'search',
  flow: 'flow',
  cart: 'cart',
  packSorting: 'pack-sort',
  withDate: 'with-date',
  detail: 'detail',
  sortBy: 'sort-by',
  machinesGroup: 'machines-group',
  packagingGroup: 'packaing-group',
  status: 'status',
  orderDisplay: 'order-display',
  scheduling: 'tab',
  setting: 'setting',
  skidPanel: 'skid',
  bundleScan: 'bundle',
  processTime: 'process-time'
} as const

export const PROCESS_TIME_OPTIONS = [
  'CUTTIME',
  'PUNCHTIME',
  'FT',
  'VB',
  'PKGT',
  'ASSEMBLYTIME',
  'SOFFITTIME',
  'TRIMACCESSTIME',
  'BBTIME'
] as const

export type ProcessTimeOption = (typeof PROCESS_TIME_OPTIONS)[number]

export const PROCESS_TIME_LABELS: Record<ProcessTimeOption, string> = {
  CUTTIME: 'Cut',
  PUNCHTIME: 'Punch',
  FT: 'FT',
  VB: 'VB',
  PKGT: 'Packaging',
  ASSEMBLYTIME: 'Assembly',
  SOFFITTIME: 'Soffit',
  TRIMACCESSTIME: 'Trim Access',
  BBTIME: 'BB'
}

const ORDER_VIEWS = ['board', 'list'] as const
const SORT_OPTIONS = ['ship', 'inv-date', 'color-gauge', 'min-time'] as const
const PACK_SORT_OPTIONS = [
  'all',
  'order',
  'carts',
  'color-gauge',
  'ship',
  'customer',
  'processed',
  'unprocessed'
] as const
const GROUP_OPTIONS = ['cart', 'order', 'color-gauge'] as const
const PACKAGING_GROUP_OPTIONS = ['order', 'color-gauge', 'customer'] as const
const DISPLAY_OPTIONS = ['orders', 'products', 'color-gauge', 'complete'] as const
export const SCHEDULING_TABS_OPTIONS = ['scheduling', 'unscheduled', 'items', 'completed'] as const
export const PACKAGING_STATUS_OPTIONS = ['all', 'not_started', 'in_progress', 'complete'] as const
export const SETTING_TABS_OPTIONS = ['machines', 'carts', 'working-hours', 'weekends', 'colors'] as const

export const SHIP_VIA_OPTIONS = ['all', 'Delivery', 'Pick up', 'SET OUTSIDE'] as const

export const ASSIGNED_OPTIONS = ['all', 'assigned', 'unassigned'] as const

type ValueOf<T extends readonly string[]> = T[number]

export type SortOptionKey = ValueOf<typeof SORT_OPTIONS>
export type PackSortOptionKey = ValueOf<typeof PACK_SORT_OPTIONS>
export type GroupOptionKey = ValueOf<typeof GROUP_OPTIONS>
export type PackagingGroupOptionKey = ValueOf<typeof PACKAGING_GROUP_OPTIONS>
export type DisplayOptionKey = ValueOf<typeof DISPLAY_OPTIONS>
export type SchedulingOptionKey = ValueOf<typeof SCHEDULING_TABS_OPTIONS>
export type ShipDate = Date | 'all'

export const useProdDate = (defaultValue = new Date()) => {
  const parse = (value: string) => {
    return parseISO(value)
  }

  const serialize = (value: Date) => {
    return formatISO(value, { representation: 'date' })
  }

  const [date, setDate] = useQueryState<Date>(QUERY_KEYS.date, {
    parse,
    serialize,
    defaultValue
  })

  const formattedDate = formatISO(date, { representation: 'date' })

  return { date, setDate, formattedDate } as const
}

function getNearestEnabledDay(d: Date): Date {
  if (!isWeekend(d)) return d
  if (d.getDay() === 6) return addDays(d, -1)
  if (d.getDay() === 0) return addDays(d, 1)
  return d
}

export const useShipVia = () =>
  useQueryState(QUERY_KEYS.shipVia, parseAsStringLiteral(SHIP_VIA_OPTIONS).withDefault(SHIP_VIA_OPTIONS[0]))

export const useShipDate = (defaultValue?: ShipDate) => {
  const [tab] = useSchedulingTabs()
  const fallback: ShipDate = defaultValue ?? (tab === 'items' ? 'all' : getNearestEnabledDay(new Date()))
  return useShipDateWithDefault(fallback)
}

const useShipDateWithDefault = (defaultValue: ShipDate) => {
  const parse = (value: string): ShipDate => {
    if (value === 'all') return 'all'
    return parseISO(value)
  }

  const serialize = (value: ShipDate): string => {
    if (value === 'all') return 'all'
    return formatISO(value, { representation: 'date' })
  }

  const [date, setDate] = useQueryState<ShipDate>(QUERY_KEYS.shipDate, {
    parse,
    serialize,
    defaultValue
  })

  const formattedDate = date === 'all' ? 'all' : formatISO(date, { representation: 'date' })

  const setAll = () => setDate('all')

  return { date, setDate, setAll, formattedDate } as const
}

export const useOrderView = () =>
  useQueryState(QUERY_KEYS.orderView, parseAsStringLiteral(ORDER_VIEWS).withDefault(ORDER_VIEWS[0]))

export const useSearch = () => useQueryState(QUERY_KEYS.search, parseAsString.withDefault(''))

export const useFlow = () => useQueryState(QUERY_KEYS.flow, parseAsString.withDefault('all'))
export const useCart = () => useQueryState(QUERY_KEYS.cart, parseAsString.withDefault('all'))

export const useWithDate = () => useQueryState(QUERY_KEYS.withDate, parseAsBoolean.withDefault(false))

export const useDetailId = () => useQueryState(QUERY_KEYS.detail, parseAsString.withDefault(''))

/**
 * Open skid details sheet (URL `?skid=<key>`). The key is either the badge label /
 * `skid.name` (preferred — what's encoded in printed QR codes) or the numeric id.
 * The sheet resolves the key to a real skid via name lookup or id parse.
 * Pass `null` to close.
 */
export const useSkidPanelKey = () =>
  useQueryState(QUERY_KEYS.skidPanel, parseAsString)

/** Bundle-scan two-step flow (URL `?bundle=<id>`). Pass `null` to close. */
export const useBundleScanId = () =>
  useQueryState(QUERY_KEYS.bundleScan, parseAsInteger)

export const useSort = () =>
  useQueryState(QUERY_KEYS.sortBy, parseAsStringLiteral(SORT_OPTIONS).withDefault(SORT_OPTIONS[0]))

export const usePackSort = () =>
  useQueryState(
    QUERY_KEYS.packSorting,
    parseAsStringLiteral(PACK_SORT_OPTIONS).withDefault(PACK_SORT_OPTIONS[0])
  )

export const useMachinesGroupOptions = ({ defaultValue }: { defaultValue: GroupOptionKey }) =>
  useQueryState(QUERY_KEYS.machinesGroup, parseAsStringLiteral(GROUP_OPTIONS).withDefault(defaultValue))

export const usePackagingGroupOptions = () =>
  useQueryState(
    QUERY_KEYS.packagingGroup,
    parseAsStringLiteral(PACKAGING_GROUP_OPTIONS).withDefault(PACKAGING_GROUP_OPTIONS[0])
  )

export const useAssigned = () => useQueryState(QUERY_KEYS.assigned, parseAsBoolean)

export const useOrderDisplayOptions = () =>
  useQueryState(
    QUERY_KEYS.orderDisplay,
    parseAsStringLiteral(DISPLAY_OPTIONS).withDefault(DISPLAY_OPTIONS[0])
  )

export const useSchedulingTabs = () =>
  useQueryState(
    QUERY_KEYS.scheduling,
    parseAsStringLiteral(SCHEDULING_TABS_OPTIONS).withDefault(SCHEDULING_TABS_OPTIONS[0])
  )

export const useSettingTabs = () =>
  useQueryState(
    QUERY_KEYS.setting,
    parseAsStringLiteral(SETTING_TABS_OPTIONS).withDefault(SETTING_TABS_OPTIONS[0])
  )

export const useProcessTime = () =>
  useQueryState(
    QUERY_KEYS.processTime,
    parseAsArrayOf(parseAsStringLiteral(PROCESS_TIME_OPTIONS)).withDefault([])
  )

export const usePackagingStatusOptions = () =>
  useQueryState(QUERY_KEYS.status, parseAsStringLiteral(PACKAGING_STATUS_OPTIONS).withDefault('all'))
